import colorVars from "@/assets/styles/colorVars"
import { Banner, IBanner } from "@/components/banner"
import { CustomBackground, blurProps } from "@/components/bg/blur-bg"
import { BlueButton } from "@/components/button"
import Heading from "@/components/heading"
import { PillTag } from "@/components/pill-tag"
import { Spacer } from "@/components/spacer"
import { useWindowSize } from "@/utilities/helpers"
import React, { FC, useEffect, useState } from "react"
import { up } from "styled-breakpoints"
import styled, { css } from "styled-components"

interface IHero {
  banner?: IBanner
  link?: Queries.WpAcfLink
  pill: string
  subtitle: string
  subtitleAddition?: string
  title: string
}

export interface HeroProps {
  className?: string
  data?: IHero
}

const Wrapper = styled.div<HeroProps>`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 24px;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 0 16px;
  margin: 0 auto;

  ${up("md")} {
    gap: 36px;
  }

  ${up("lg")} {
    max-width: calc(1120px + 32px);
  }
`

const StyledCustomBackground = styled(CustomBackground)<any>`
  opacity: 0;
  transition: opacity 0.7s ease-in-out;

  ${(props) =>
    props.isVisible &&
    css`
      opacity: 1;
    `}
`

const StyledPillTag = styled(PillTag)`
  height: 32px;
  margin-top: 16px;
`

const CustomHeading = styled(Heading)`
  max-width: 1100px;

  > span {
    margin-top: -8px;
  }

  ${up("md")} {
    min-height: 160px;
  }

  ${up("lg")} {
    min-height: 210px;
    margin-top: -25px;
  }
`

const Description = styled.p`
  margin: -8px 0 0;
  font-size: 18px;
  font-style: normal;
  font-weight: 400;
  line-height: 26px;
  color: #b4bcd0;
  text-align: center;
  letter-spacing: -0.1px;

  ${up("lg")} {
    margin: -25px 0 0;
    font-size: 22px;
    line-height: 30px;
    letter-spacing: 0;
  }
`

const GSpot = styled.span`
  font-size: 18px;
  font-style: normal;
  font-weight: 600;
  line-height: 26px;
  background: linear-gradient(270deg, #bec5ff 0%, #fff 100%);
  /* stylelint-disable-next-line */
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;

  ${up("md")} {
    font-size: 22px;
    line-height: 30px;
    letter-spacing: 0;
  }
`

const StyledBlueButton = styled(BlueButton)`
  padding: 13px 20px;
`

const heroBlurList: Array<blurProps> = [
  {
    blur: "180px",
    color: colorVars.teal.main,
    opacity: 0.45,
    pos: {
      bottom: "initial",
      left: "2%",
      right: "initial",
      top: "32%",
    },
    size: "160px",
  },
  {
    blur: "100px",
    color: colorVars.blue.p11,
    opacity: 0.15,
    pos: {
      bottom: "initial",
      left: "15%",
      right: "initial",
      top: "33%",
    },
    size: "250px",
  },
  {
    blur: "135px",
    color: colorVars.pink.p11,
    opacity: 0.6,
    pos: {
      bottom: "initial",
      left: "initial",
      right: "-8%",
      top: "30%",
    },
    size: "160px",
  },
  {
    blur: "110px",
    color: colorVars.blue.p11,
    opacity: 0.85,
    pos: {
      bottom: "initial",
      left: "initial",
      right: "-5%",
      top: "50%",
    },
    size: "100px",
  },
]

const heroBlurListSmall: Array<blurProps> = [
  {
    blur: "100px",
    color: colorVars.teal.main,
    opacity: 0.5,
    pos: {
      bottom: "initial",
      left: "2%",
      right: "initial",
      top: "23%",
    },
    size: "90px",
  },
  {
    blur: "50px",
    color: colorVars.blue.p11,
    opacity: 0.1,
    pos: {
      bottom: "initial",
      left: "5%",
      right: "initial",
      top: "15%",
    },
    size: "125px",
  },
  {
    blur: "75px",
    color: colorVars.pink.p9,
    opacity: 0.6,
    pos: {
      bottom: "initial",
      left: "initial",
      right: "-8%",
      top: "33%",
    },
    size: "125px",
  },
  {
    blur: "55px",
    color: colorVars.blue.p11,
    opacity: 0.95,
    pos: {
      bottom: "initial",
      left: "initial",
      right: "0",
      top: "50%",
    },
    size: "60px",
  },
]

export const Hero: FC<HeroProps> = (props) => {
  const { className, data } = props

  if (!data) return null

  const { banner, link, pill, subtitle, subtitleAddition, title } = data

  const { width: windowWidth } = useWindowSize()
  const isSmallViewport = windowWidth < 768

  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    setIsVisible(true)
  }, [])

  return (
    <Wrapper className={className && className !== "" ? className : ""}>
      <StyledCustomBackground
        blurs={isSmallViewport ? heroBlurListSmall : heroBlurList}
        isVisible={isVisible}
      />
      <Spacer height={{ default: "24px", md: "32px" }} />
      {banner?.text && <Banner data={banner} />}
      <StyledPillTag color="sparkles" title={pill} />
      <CustomHeading
        center
        preLine
        size="hero"
        splitNewline
        title={title}
        titleTag="h1"
        whiteGradient
      />
      <Description>
        {subtitle}
        {subtitleAddition && <GSpot>&nbsp;{subtitleAddition}</GSpot>}
      </Description>
      <StyledBlueButton center link={link} type="url" />
      <Spacer height={{ default: "22px", md: "0" }} />
    </Wrapper>
  )
}
